import { Container, Sprite } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { CoverBackground, sizedVariant, spriteVariant } from "@slot/presentation";
import type { Log, SpineDriver, ViewportStore } from "@slot/presentation";

/** The `spineBackground` prefab box the `BG.skeleton` sits in (cover-scaled as a unit). */
const SPINE_BACKGROUND_BOX = { width: 1441.712, height: 1103.111 };
const SPINE_BACKGROUND_SKELETON = "animations/spine/BG.skeleton";
const SPINE_BACKGROUND_LOOP = "animation";

/**
 * The scene's `BackgroundLayer`: one cover-fit root per device variant hosting the animated Spine
 * background (`BG.skeleton`), falling back to the static (brandable) `backgrounds/background` sprite
 * when the skeleton is missing. `overlayTarget` is what the anticipation soft-light ramps.
 */
export class GameBackground extends Container {
  public readonly cover: CoverBackground;
  /** The active variant's root — the anticipation overlay filter goes here. */
  public readonly overlayTarget: Container;
  private readonly spines: Spine[] = [];

  public constructor(input: { assets: GameVisualAssets; spineDriver: SpineDriver; log: Log }) {
    super({ label: "BackgroundLayer" });
    const skeleton = input.assets.skeletons.get(SPINE_BACKGROUND_SKELETON);
    const variant = (): ReturnType<typeof sizedVariant> | ReturnType<typeof spriteVariant> | null => {
      if (skeleton) {
        const box = new Container({ label: "spineBackground" });
        const spine = new Spine({ skeletonData: skeleton, autoUpdate: false });
        spine.label = "content";
        input.spineDriver.attach(spine);
        box.addChild(spine);
        this.spines.push(spine);
        return sizedVariant(box, SPINE_BACKGROUND_BOX.width, SPINE_BACKGROUND_BOX.height, {
          onActivate: () => {
            if (spine.skeleton.data.findAnimation(SPINE_BACKGROUND_LOOP) && !spine.state.getCurrent(0)) {
              spine.state.setAnimation(0, SPINE_BACKGROUND_LOOP, true);
            }
          },
        });
      }
      const texture = input.assets.textures.background;
      if (!texture) return null;
      return spriteVariant(new Sprite(texture));
    };
    // The scene dump authored three roots (desktop / portrait / landscape) all hosting the same
    // prefab with no per-variant offsets, so one shared variant is equivalent.
    const shared = variant();
    if (!shared) input.log("[GameBackground] no background art loaded");
    this.cover = new CoverBackground({ variants: shared ? { shared } : {} });
    this.addChild(this.cover);
    this.overlayTarget = shared ? shared.target : this.cover;
  }

  public bind(viewport: ViewportStore): void {
    this.cover.bind(viewport);
  }

  public dispose(): void {
    this.cover.dispose();
    for (const spine of this.spines) if (!spine.destroyed) spine.destroy();
    this.destroy({ children: true });
  }
}
